import { find, isNull, result } from "lodash-es";
// icons
import { Info } from "lucide-react";
import moment from "moment-timezone";
import { FC, useContext, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";

// templates
import {
  captureProductEvent,
  productAnalyticsEvents,
} from "../../analytics/events";
import LoadingScreen from "../../components/LoadingScreen";
import Page from "../../components/Page";
import { UserContext } from "../../contexts/UserContext";
import sendStandardEvent from "../../hooks/use-firebase-event";
import useServerNotifications from "../../hooks/use-server-notifications";
import { TopNav } from "../../layouts/dashboard/top-nav";
import { setClusterAndOrg, setOrgId } from "../../slices/current";
import { pushSuccess, pushWarning } from "../../slices/notifications";
import { setOrgClusterOpen } from "../../slices/organizations";
import { useDispatch } from "../../store";
// utilities
import {
  useAcceptInvitationMutation,
  useCompleteOnboardingMutation,
  useConfigureOnboardingMutation,
  useGetClusterQuery,
  useListOrganizationsQuery,
  useListUserInvitationsQuery,
  useRejectInvitationMutation,
  useStartOnboardingMutation,
} from "../../store/pomeriumApi";
import ClusterOnboardingWizard, {
  SystemType,
} from "@/components/ClusterOnboardingWizard";
// templates
import OrganizationInvites from "@/components/OrganizationInvites";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
// base components
import { Button } from "@/components/ui/button";

const Onboarding: FC = () => {
  // --- APP HOOKS
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { user } = useContext(UserContext);

  // --- ONBOARDING MUTATIONS
  const [startOnboarding, { data: startOnboardingResult }] =
    useStartOnboardingMutation();
  const [completeOnboarding] = useCompleteOnboardingMutation({
    fixedCacheKey: "complete-onboarding",
  });
  const [configureOnboarding] = useConfigureOnboardingMutation();

  // --- ORG/CLUSTER
  const { data: organizations } = useListOrganizationsQuery(undefined, {
    skip: !user?.id,
  });
  const { data: cluster, isLoading: clusterLoading } = useGetClusterQuery(
    {
      organizationId: startOnboardingResult?.organizationId || "",
      clusterId: startOnboardingResult?.clusterId || "",
    },
    {
      skip:
        !user?.id ||
        !startOnboardingResult?.organizationId ||
        !startOnboardingResult?.clusterId,
    },
  );
  useServerNotifications(startOnboardingResult?.organizationId);

  // --- INVITES
  const { data: invites, isLoading: invitesLoading } =
    useListUserInvitationsQuery(undefined, {
      skip: !user?.id,
    });

  const [accept] = useAcceptInvitationMutation();
  const [reject] = useRejectInvitationMutation();

  const accepted = (inviteID: string, invitedBy: string) => {
    accept(inviteID);
    dispatch(pushSuccess("You have accepted an invitation from " + invitedBy));
    sendStandardEvent("Organization Invite Accepted", "onboarding", user);
  };
  const rejected = (inviteID: string, invitedBy: string) => {
    reject(inviteID);
    dispatch(pushWarning("You have rejected an invitation from " + invitedBy));
    sendStandardEvent("Organization Invite Rejected", "onboarding", user);
  };

  // --- WIZARD/UI STATES
  const [loading, setLoading] = useState(true);
  const [showInvites, setShowInvites] = useState<null | boolean>(null);
  const [showSampleRoutes, setShowSampleRoutes] = useState(false);

  // --- FUNCTIONS AND HANDLERS
  function openClusterOrgModal() {
    setLoading(true);
    completeOnboarding();
    const professionOrgId =
      (result(
        find(organizations || [], function (obj) {
          return obj.organizationType === "professional";
        }),
        "id",
      ) as string) || "";
    if (professionOrgId) {
      dispatch(setOrgId(professionOrgId));
    }
    sendStandardEvent("Invite Accepted Tutorial Skipped", "onboarding", user);
    dispatch(setOrgClusterOpen(true));
  }

  function handleChangeAddressAndPort(
    address: string | undefined,
    port: string | undefined,
    systemType: SystemType | undefined,
  ): void {
    sendStandardEvent("Address and port change", "onboarding", user, {
      address,
      port,
    });
    configureOnboarding({
      ipAddress: address,
      port: port,
      system: systemType || "",
      timezone: moment.tz.guess(),
    });
  }

  function handleChangeSystemType(value: SystemType) {
    configureOnboarding({
      system: value,
      timezone: moment.tz.guess(),
    });
  }

  function finishOnboarding() {
    setLoading(true);
    setShowSampleRoutes(true);
    sendStandardEvent("Tutorial Complete", "onboarding", user);
    dispatch(
      setClusterAndOrg({
        clusterId: startOnboardingResult?.clusterId || "",
        organizationId: startOnboardingResult?.organizationId || "",
      }),
    );
    completeOnboarding();
  }

  // --- EFFECTS
  useEffect(() => {
    if (invites?.length && isNull(showInvites)) {
      setShowInvites(true);
    }
  }, [invites]);

  useEffect(() => {
    if (!invitesLoading && !clusterLoading) {
      setLoading(false);
    }
  }, [invitesLoading, clusterLoading]);

  useEffect(() => {
    const promise = startOnboarding({
      system: "docker", // initial systemType is Docker
      timezone: moment.tz.guess(),
    });
    if (localStorage.getItem("user-onboarded" + user?.id) === null) {
      localStorage.setItem("user-onboarded" + user?.id, "true");
      captureProductEvent(productAnalyticsEvents.activationOnboardingStarted);
      sendStandardEvent("Tutorial Begin", "onboarding", user);
    }

    return () => {
      promise.abort();
    };
  }, [startOnboarding]);

  useEffect(() => {
    if (!user?.needsOnboarding) {
      navigate("/app/management/routes", {
        replace: true,
        state: {
          sampleBanner: showSampleRoutes,
        },
      });
    }
  }, [JSON.stringify(user)]);

  // --- PAGE UI
  if (loading) {
    return <LoadingScreen />;
  }

  if (showInvites) {
    return (
      <>
        <TopNav hideNav />
        <Page title="Onboarding">
          <div className="mt-16 w-full">
            <OrganizationInvites
              invites={invites}
              accepted={accepted}
              rejected={rejected}
              setShowInvites={setShowInvites}
              openClusterOrgModal={openClusterOrgModal}
            />
          </div>
        </Page>
      </>
    );
  }

  return (
    <>
      <TopNav hideNav />
      <Page title="Onboarding">
        <div className="mt-16 w-full flex flex-col gap-4">
          <ClusterOnboardingWizard
            cluster={cluster}
            clusterId={startOnboardingResult?.clusterId}
            clusterToken={startOnboardingResult?.clusterToken}
            organizationId={startOnboardingResult?.organizationId}
            onFinishStep={finishOnboarding}
            handleChangeSystemType={handleChangeSystemType}
            handleChangeAddressAndPort={handleChangeAddressAndPort}
          />

          {(invites ?? []).length > 0 && (
            <Alert className="w-full lg:max-w-6xl mx-auto">
              <Info />
              <AlertTitle>You have invites!</AlertTitle>
              <AlertDescription className="flex flex-col gap-4">
                Existing Pomerium organizations have invited you to join them!
                <Button
                  onClick={() => setShowInvites(true)}
                  className="w-fit self-end"
                >
                  Manage Invites
                </Button>
              </AlertDescription>
            </Alert>
          )}
        </div>
      </Page>
    </>
  );
};

export default Onboarding;
